// src/oura/screens/FormAnswers.tsx
//
// Form answers (#4065): every answer to the onboarding form, and whether it
// reached Oura.
//
// Each submit of the form posts the answer to Oura. When that run fails,
// nothing in Oura used to say so: the client's Onboarding tab simply stayed
// "Not answered yet", and the only way to find out was to open the form in
// Google. This screen reads the form from Oura's side, so an answer that never
// arrived is a row with a button on it.
//
// Staff only, no business in focus. It decides nothing: the rows, the counts
// and every refusal are the server's, and each action is one POST.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2Icon, DownloadCloudIcon, InboxIcon, Loader2Icon, PlugZapIcon, UserPlusIcon } from "lucide-react";
import BreadcrumbNav from "@component/breadcrumb-nav";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@component/select";
import { useSnackbar } from "@hook/use-snackbar";
import { Drawer, DrawerBlock, PillButton, SURFACE, TYPE } from "@oura/ui";
import { listOnboardings, type AnswerDismissal } from "@service/aeo/onboarding";
import {
    assignFormResponse,
    dismissFormResponse,
    isFormResponsesLive,
    listFormResponses,
    reprocessFormResponse,
    type FormResponseItem,
    type FormResponseList,
    type FormResponsesRead,
} from "@service/aeo/formResponses";
import { cn } from "../lib/cn";
import { ouraHref } from "../nav";
import { Button } from "../primitives/Button";
import { Card } from "../primitives/Card";
import { Chip } from "../primitives/Chip";
import { EmptyState } from "../primitives/EmptyState";
import { PageHeader } from "../primitives/PageHeader";
import { Pagination } from "../primitives/Pagination";
import { Field, Refusal } from "../components/onboarding/fields";
import { DismissAnswer } from "../components/onboarding/UnmatchedAnswers";
import { assignTargets, dayMonth, dayTime, type AssignTarget } from "../onboarding/derive";
import { attemptLine, countFor, sinceDate, statusesFor, STATUS_CHIP, STATUS_LABEL, summaryAfter, type FormAnswersView } from "../onboarding/formAnswers";

const PAGE_SIZE = 20;
/** The most the route returns in one read. */
const LIMIT = 200;
const WINDOWS = [7, 30, 90] as const;
const TH = "h-[45px] whitespace-nowrap px-4 py-3 text-xs font-medium uppercase tracking-wider text-gray-500";
const DASH = <span className="text-gray-400">—</span>;
const plural = (n: number) => `${n} ${n === 1 ? "answer" : "answers"}`;

export function FormAnswers() {
    const snackbar = useSnackbar();
    const [view, setView] = useState<FormAnswersView>("needs");
    const [days, setDays] = useState<number>(30);
    const [page, setPage] = useState(1);
    /** Null while a read is in flight. */
    const [read, setRead] = useState<FormResponsesRead | null>(null);
    const [targets, setTargets] = useState<AssignTarget[]>([]);
    const [assigning, setAssigning] = useState<FormResponseItem | null>(null);
    // The set is the guard and the state only draws it: a second click lands
    // before React has re-rendered the disabled button.
    const inFlight = useRef(new Set<string>());
    const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());
    /** The read that last STARTED wins, so a slow one cannot land over a newer filter. */
    const readAt = useRef(0);
    /** Whether the table on screen is a read that has landed, so a write has a row to settle. */
    const readLanded = useRef(false);
    /** The table on screen, for `settle` to ask whether it holds the row a write changed. */
    const readRef = useRef<FormResponsesRead | null>(null);
    readRef.current = read;

    const since = useMemo(() => sinceDate(days), [days]);

    const load = useCallback(async () => {
        const mine = ++readAt.current;
        readLanded.current = false;
        setRead(null);
        const r = await listFormResponses({ status: statusesFor(view), since, limit: LIMIT });
        if (mine === readAt.current) {
            readLanded.current = r.ok;
            setRead(r);
        }
    }, [view, since]);

    useEffect(() => {
        setPage(1);
        void load();
    }, [load]);

    // The clients an unmatched answer can be filed on. Never throws.
    const loadTargets = useCallback(async () => {
        const list = await listOnboardings();
        setTargets(assignTargets(Object.fromEntries(list.map((o) => [o.businessId, o]))));
    }, []);
    useEffect(() => void loadTargets(), [loadTargets]);

    /**
     * One row changed on this screen: the row and the counts move together, with no second read.
     * The row stays where it is, as its new chip, until the next read: it is how staff see what their
     * click did, so a filtered view is not re-read after each write.
     */
    const settle = useCallback((responseId: string, change: Partial<FormResponseItem>) => {
        // No row to update: a read is in flight or failed, or one landed that does not hold this row
        // (the filter changed while the write ran, and that read may have started before the write
        // finished, so the row may now belong in it). Read again, which also drops any older read.
        const shown = readRef.current;
        if (!readLanded.current || !shown?.ok || !shown.data.items.some((i) => i.responseId === responseId)) {
            void load();
            return;
        }
        setRead((r) => {
            if (!r?.ok) return r;
            const was = r.data.items.find((i) => i.responseId === responseId);
            if (!was) return r;
            const data: FormResponseList = {
                ...r.data,
                items: r.data.items.map((i) => (i.responseId === responseId ? { ...i, ...change } : i)),
                summary: summaryAfter(r.data.summary, was.status, change.status ?? was.status),
            };
            return { ok: true, data };
        });
    }, [load]);

    const fetchFromGoogle = async (row: FormResponseItem) => {
        if (inFlight.current.has(row.responseId)) return;
        inFlight.current.add(row.responseId);
        setBusy(new Set(inFlight.current));
        try {
            const r = await reprocessFormResponse(row.responseId);
            if (!r.ok) {
                // The backend's sentence as it comes, and the row as it was.
                snackbar.showError(r.message);
                return;
            }
            const { status, matched, businessId, businessName, submissionId, alreadyStored } = r.data;
            settle(row.responseId, {
                status,
                businessId,
                businessName,
                submissionId,
                // What this click did, in the words the backend records it with.
                lastAttempt: { at: new Date().toISOString(), source: "staff", outcome: alreadyStored ? "duplicate" : matched ? "filed" : "unmatched", errorCode: null, message: null },
            });
            if (alreadyStored) snackbar.showSuccess("This answer was already in Oura.");
            else if (matched) snackbar.showSuccess(`Filed on ${businessName ?? "the client"}.`);
            else snackbar.showSuccess("Fetched, but the website matches no client.");
        } finally {
            inFlight.current.delete(row.responseId);
            setBusy(new Set(inFlight.current));
        }
    };

    const data = read?.ok ? read.data : null;
    const rows = data?.items ?? [];
    const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const sinceLabel = dayMonth(since);

    return (
        <div>
            <div className="mb-3">
                <BreadcrumbNav items={[{ label: "Businesses", href: ouraHref("businesses", { role: "oura-staff" }) }, { label: "Form answers" }]} />
            </div>
            <PageHeader
                title="Form answers"
                subtitle="Every answer to the onboarding form, and whether it reached Oura."
                action={
                    // Which form this is, so a staging answer is never mistaken for a real one.
                    !isFormResponsesLive ? (
                        <span className="text-sm text-amber-700">Sample answers. The form is not connected yet.</span>
                    ) : data ? (
                        <span className="text-sm text-gray-500">Reading the {data.environment} form</span>
                    ) : undefined
                }
            />

            {read && !read.ok && read.kind !== "unavailable" ? (
                // Not set up, or not allowed: nothing to list and nothing to retry.
                <Card>
                    <EmptyState icon={PlugZapIcon} title={read.kind === "not_configured" ? "The form is not connected yet" : "You cannot open this"} description={read.message} />
                </Card>
            ) : (
                <>
                    {data && (
                        <p className="mb-4 text-sm text-gray-700">
                            <span className="tabular-nums">{plural(data.summary.total)}</span> since {sinceLabel}: <span className="tabular-nums">{data.summary.filed}</span> filed,{" "}
                            <CountFilter count={data.summary.unmatched} label="matched no client" onPick={() => setView("unmatched")} />,{" "}
                            <CountFilter count={data.summary.notReceived} label="not received" onPick={() => setView("not_received")} />
                            {(data.summary.dismissed ?? 0) > 0 && (
                                <>
                                    , <CountFilter count={data.summary.dismissed ?? 0} label="dismissed" onPick={() => setView("dismissed")} />
                                </>
                            )}
                            .
                        </p>
                    )}

                    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
                        <Select value={view} onValueChange={(v) => setView(v as FormAnswersView)}>
                            <SelectTrigger aria-label="Status" className="w-full flex-shrink-0 text-sm sm:w-[240px]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {(["needs", "all", "not_received", "unmatched", "dismissed", "filed"] as FormAnswersView[]).map((v) => {
                                    const count = data ? countFor(v, data.summary) : null;
                                    // A single status with nothing in it is not offered; the two views always are.
                                    if (count === 0 && v !== "needs" && v !== "all" && v !== view) return null;
                                    return (
                                        <SelectItem key={v} value={v}>
                                            {v === "needs" ? "Needs attention" : v === "all" ? "All answers" : STATUS_LABEL[v]}
                                            {count !== null && <span className="text-gray-400"> ({count})</span>}
                                        </SelectItem>
                                    );
                                })}
                            </SelectContent>
                        </Select>
                        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
                            <SelectTrigger aria-label="Since" className="w-full flex-shrink-0 text-sm sm:w-[180px]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {WINDOWS.map((d) => (
                                    <SelectItem key={d} value={String(d)}>
                                        Last {d} days
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {read && !read.ok ? (
                        <div role="alert" className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3">
                            <span className="min-w-0 flex-1 text-sm text-rose-700">{read.message}</span>
                            <Button variant="outline" size="sm" onClick={() => void load()}>
                                Try again
                            </Button>
                        </div>
                    ) : data && rows.length === 0 ? (
                        <Card>
                            {data.summary.total === 0 ? (
                                <EmptyState icon={InboxIcon} title={`No one has answered the form since ${sinceLabel}.`} />
                            ) : (
                                <EmptyState
                                    icon={CheckCircle2Icon}
                                    title={view === "needs" ? `Every answer since ${sinceLabel} is filed.` : `No answers with this status since ${sinceLabel}.`}
                                    action={
                                        <Button variant="link" onClick={() => setView("all")}>
                                            Show all answers
                                        </Button>
                                    }
                                />
                            )}
                        </Card>
                    ) : (
                        <Card className="overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-max">
                                    <thead>
                                        <tr className="border-b border-gray-200 bg-gray-50">
                                            <th className={cn(TH, "text-left")}>Submitted</th>
                                            <th className={cn(TH, "text-left")}>From</th>
                                            <th className={cn(TH, "text-left")}>Website</th>
                                            <th className={cn(TH, "text-center")}>Status</th>
                                            <th className={cn(TH, "text-left")}>Client</th>
                                            <th className={cn(TH, "text-center")}>Files</th>
                                            <th className={cn(TH, "text-left")}>Last attempt</th>
                                            <th className={cn(TH, "text-right")}>
                                                <span className="sr-only">Action</span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {!data
                                            ? [0, 1, 2, 3, 4].map((i) => (
                                                  <tr key={i} aria-hidden>
                                                      {[0, 1, 2, 3, 4, 5, 6, 7].map((c) => (
                                                          <td key={c} className="px-4 py-4">
                                                              <div className="h-4 w-24 animate-pulse rounded bg-gray-100" />
                                                          </td>
                                                      ))}
                                                  </tr>
                                              ))
                                            : pageRows.map((row) => (
                                                  <AnswerRow
                                                      key={row.responseId}
                                                      row={row}
                                                      busy={busy.has(row.responseId)}
                                                      onFetch={() => void fetchFromGoogle(row)}
                                                      onAssign={() => setAssigning(row)}
                                                  />
                                              ))}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    )}

                    {data && <Pagination page={page} total={rows.length} pageSize={PAGE_SIZE} itemLabel="answers" onPageChange={setPage} className="mt-4" />}
                    {/* The route returns the newest LIMIT: say so when the window holds more than is listed. */}
                    {data && countFor(view, data.summary) > rows.length && rows.length >= LIMIT && (
                        <p className="mt-3 text-xs text-gray-500">Showing the newest {rows.length}. Pick a shorter window to see the rest.</p>
                    )}
                </>
            )}

            <AssignDrawer
                row={assigning}
                targets={targets}
                onClose={() => setAssigning(null)}
                onFiled={(row, client) => {
                    settle(row.responseId, {
                        status: "filed",
                        businessId: client.businessId,
                        businessName: client.name,
                        lastAttempt: { at: new Date().toISOString(), source: "staff", outcome: "filed", errorCode: null, message: null },
                    });
                    snackbar.showSuccess(`Filed on ${client.name}.`);
                    setAssigning(null);
                    // That client is no longer waiting for a form.
                    void loadTargets();
                }}
                onDismissed={(row, dismissed) => {
                    settle(row.responseId, { status: "dismissed", dismissed });
                    snackbar.showSuccess("Answer dismissed.");
                    setAssigning(null);
                }}
            />
        </div>
    );
}

/** A problem count in the summary line: a filter when there is something to see, plain text when there is not. */
function CountFilter({ count, label, onPick }: { count: number; label: string; onPick: () => void }) {
    if (count === 0) return <span className="tabular-nums">0 {label}</span>;
    return (
        <button type="button" onClick={onPick} className="rounded font-medium tabular-nums text-blue-700 underline decoration-blue-300 underline-offset-2 hover:text-blue-800">
            {count} {label}
        </button>
    );
}

function AnswerRow({ row, busy, onFetch, onAssign }: { row: FormResponseItem; busy: boolean; onFetch: () => void; onAssign: () => void }) {
    const attempt = row.lastAttempt;
    return (
        <tr className="transition-colors hover:bg-gray-50">
            <td className="whitespace-nowrap px-4 py-4">
                <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50">
                        <InboxIcon className="h-4 w-4 text-blue-600" />
                    </div>
                    <span className="text-sm font-medium text-gray-900">{dayTime(row.submittedAt) || DASH}</span>
                </div>
            </td>
            <td className="px-4 py-4 text-sm text-gray-700">{row.respondentEmail?.trim() || DASH}</td>
            <td className="px-4 py-4 text-sm text-gray-700">{row.website?.trim() || DASH}</td>
            <td className="px-4 py-4 text-center">
                <Chip className={STATUS_CHIP[row.status] ?? STATUS_CHIP.unmatched}>{STATUS_LABEL[row.status] ?? row.status}</Chip>
                {!row.inGoogle && <div className="mt-1 text-xs text-gray-500">No longer in Google</div>}
                {/* Who set it aside and why (#4641): the reason is the record, so it is on the row. */}
                {row.status === "dismissed" && row.dismissed && (
                    <div className="mx-auto mt-1 max-w-[220px] text-xs leading-relaxed text-gray-500">
                        {row.dismissed.by ? `By ${row.dismissed.by}` : "Dismissed"}
                        {row.dismissed.reason ? ` · ${row.dismissed.reason}` : ""}
                    </div>
                )}
            </td>
            <td className="px-4 py-4 text-sm">
                {row.businessId ? (
                    <Link
                        to={ouraHref("dashboard", { role: "oura-staff", businessId: row.businessId })}
                        className="font-medium text-blue-700 underline decoration-blue-300 underline-offset-2 hover:text-blue-800"
                    >
                        {row.businessName?.trim() || "Open the client"}
                    </Link>
                ) : (
                    DASH
                )}
            </td>
            <td className="px-4 py-4 text-center text-sm tabular-nums text-gray-700">{row.fileCount ?? DASH}</td>
            <td className="max-w-[280px] px-4 py-4">
                {attempt ? (
                    <>
                        <div className="whitespace-nowrap text-sm text-gray-500">{attemptLine(attempt)}</div>
                        {attempt.message && <div className="mt-0.5 text-xs leading-relaxed text-gray-500">{attempt.message}</div>}
                    </>
                ) : (
                    DASH
                )}
            </td>
            <td className="whitespace-nowrap px-4 py-4 text-right">
                {row.status === "not_received" && row.inGoogle && (
                    <Button size="sm" disabled={busy} onClick={onFetch}>
                        {busy ? <Loader2Icon className="animate-spin" /> : <DownloadCloudIcon />}
                        Fetch from Google and file
                    </Button>
                )}
                {row.status === "unmatched" && row.submissionId && (
                    <Button size="sm" variant="outline" onClick={onAssign}>
                        <UserPlusIcon />
                        Assign or dismiss
                    </Button>
                )}
            </td>
        </tr>
    );
}

/** Pick the client an unmatched answer belongs to: the assign action from #3977, on one row. */
function AssignDrawer({
    row,
    targets,
    onClose,
    onFiled,
    onDismissed,
}: {
    row: FormResponseItem | null;
    targets: AssignTarget[];
    onClose: () => void;
    onFiled: (row: FormResponseItem, client: AssignTarget) => void;
    /** Set aside with a reason (#4641): the caller settles the row. */
    onDismissed: (row: FormResponseItem, dismissed: AnswerDismissal | null) => void;
}) {
    const [picked, setPicked] = useState("");
    const [busy, setBusy] = useState(false);
    // A dismiss in flight holds the drawer too: one answer is filed OR dismissed (Jack, #4645).
    const [dismissing, setDismissing] = useState(false);
    const [refused, setRefused] = useState<string | null>(null);
    const rowId = row?.responseId;
    useEffect(() => {
        setPicked("");
        setRefused(null);
    }, [rowId]);

    const file = async () => {
        const client = targets.find((t) => t.businessId === picked);
        if (!row?.submissionId || !client || busy || dismissing) return;
        setBusy(true);
        setRefused(null);
        try {
            const r = await assignFormResponse(row.submissionId, client);
            if (r.ok) onFiled(row, client);
            else setRefused(r.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Drawer open={row !== null} onClose={onClose} title="Assign to a client" sub="This answer was sent with a website Oura does not have. Pick the client it belongs to." width={620}>
            {row && (
                <div className={cn(SURFACE.block, "space-y-3")}>
                    <div className="space-y-1">
                        <p className={cn(TYPE.tb, "m-0 break-words font-medium text-gray-900")}>{row.website?.trim() || "No website given"}</p>
                        <p className={cn(TYPE.tc, "m-0 break-words text-gray-600")}>{row.respondentEmail?.trim() || "No email given"}</p>
                        <p className={cn(TYPE.tc, "m-0 text-gray-500")}>Answered {dayTime(row.submittedAt)}</p>
                    </div>
                    {targets.length === 0 ? (
                        <DrawerBlock label="Nowhere to file it yet">No client is waiting for form answers. Create the client first, or check whether this answer has already been filed on one.</DrawerBlock>
                    ) : (
                        <Field label="File it on" hint="Only clients still waiting for a form.">
                            {({ id, describedBy }) => (
                                <select
                                    id={id}
                                    aria-describedby={describedBy}
                                    value={picked}
                                    disabled={busy || dismissing}
                                    onChange={(e) => {
                                        setPicked(e.target.value);
                                        setRefused(null);
                                    }}
                                    className={cn(TYPE.tb, "block h-11 w-full rounded-xl border-0 bg-gray-50 px-3 text-gray-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600 sm:h-9")}
                                >
                                    <option value="">Pick a client</option>
                                    {targets.map((t) => (
                                        <option key={t.businessId} value={t.businessId}>
                                            {t.name} · {t.domain}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </Field>
                    )}
                    {refused && <Refusal>{refused}</Refusal>}
                    <PillButton variant="primary" busy={busy} disabled={!picked || busy || dismissing} onClick={() => void file()}>
                        File it on this client
                    </PillButton>
                    {row.submissionId && (
                        <DismissAnswer
                            key={row.responseId}
                            answerId={row.submissionId}
                            disabled={busy}
                            dismiss={dismissFormResponse}
                            onBusyChange={setDismissing}
                            onDismissed={(dismissed) => onDismissed(row, dismissed)}
                        />
                    )}
                </div>
            )}
        </Drawer>
    );
}
